/**
 * "Where is everyone?" — the team's day on a map, inside a chat reply
 * (docs/intelligent-chat-answers-plan.md, "Map", wave 3).
 *
 * ## 🚨 Pins sit at JOBS, never at people
 *
 * Every pin is a job's address — the property's door pin when it has one,
 * otherwise its postcode's centroid — exactly as the Live Map draws it
 * (`src/lib/queries/map.ts`, `jobPoint`). Nothing here knows where a van or a
 * phone is, and no string on this block may say otherwise: no "is at", no
 * "arriving", no ETA. A person on the way is shown at the job they are going
 * TO, with its BOOKED time, which is a fact about the diary, not a prediction.
 *
 * ## Who is listed
 *
 * Everybody with a job today (assigned or supporting), plus the field team
 * (the Live Map's engineer/manager population) with nothing booked, listed as
 * "Nothing booked today" and never pinned. A job whose address cannot be
 * placed is listed and not pinned — a miss is a miss, never a guess
 * (`src/lib/travel/geocode.ts`).
 *
 * ## Placement precedence
 *
 * The same order as `placeEngineer` in map.ts: on site, then on the way, then
 * the next job not started, then the last one finished. Re-derived here only
 * because that function returns finished sentences ("On the way · due 14:00")
 * and this block needs the raw facts to word them without implying an
 * arrival time.
 *
 * Pure and directive-free (anti-pattern #18): the route, the panel, the Mucka
 * Bar and the oversight grader all import it. The Mapbox URL is built here but
 * only ever CALLED at render, with the token passed in — a stored block holds
 * coordinates and labels, never a URL and never a token.
 */

/** What a job is doing, as far as the diary knows. */
export type TeamMapStopStatus = 'on-site' | 'on-the-way' | 'not-started' | 'finished';

/** What a person is doing, as far as the diary knows. */
export type TeamMapStatus = TeamMapStopStatus | 'nothing-booked';

/** One of a person's jobs today. */
export interface TeamMapStop {
  jobId: string;
  /** Position in their day, from 1 — the number on the pin. */
  n: number;
  status: TeamMapStopStatus;
  /** The job type, or "Job". Never the customer's name. */
  title: string;
  postcode: string | null;
  /** Booked start, London "14:00". */
  booked: string | null;
  /** Null when the address could not be placed: listed, not pinned. */
  lat: number | null;
  lng: number | null;
  path: string;
}

export interface TeamMapPerson {
  userId: string;
  /** As stored on the user. The component says "You" when `isYou`. */
  name: string;
  isYou: boolean;
  status: TeamMapStatus;
  /** Index into `stops` of the job that places them; null when nothing is booked. */
  at: number | null;
  /**
   * The one time worth saying, London "12:30": when they started (on site),
   * the booked start (on the way / not started) or when they finished. Null
   * when the diary did not record it — never filled in from another field.
   */
  clock: string | null;
  stops: TeamMapStop[];
  /** Jobs beyond `stops`, counted rather than dropped. */
  stopsMore: number;
}

export interface TeamMapBlock {
  kind: 'team-map';
  /** London YYYY-MM-DD the block describes. */
  date: string;
  /** "12:42", London — when the diary was read. */
  asOf: string;
  /** "Fri 10 Oct, 12:42" — shown on an older reply, so a stale map says so. */
  asOfLabel: string;
  people: TeamMapPerson[];
  /** People beyond `people`, counted and never hidden silently. */
  more: number;
  /** Today's jobs with nobody on them (not cancelled). */
  unassigned: number;
  /** The person the question was about ("Where's Dave?"): their chip opens first. */
  focusUserId: string | null;
  /** The go-row. Null when the seat has nowhere to go with it. */
  open: { label: string; path: string } | null;
}

/** People shown as rows; the rest are counted. */
export const TEAM_MAP_MAX_PEOPLE = 12;
/** Jobs per person kept on the block; a day longer than this is a page. */
export const TEAM_MAP_MAX_STOPS = 12;

/** A job as the server read it, already classified against the account's statuses. */
export interface TeamMapJobInput {
  id: string;
  type: string | null;
  postcode: string | null;
  lat: number | null;
  lng: number | null;
  /** ISO timestamps as stored. */
  scheduledStart: string;
  actualStart: string | null;
  actualEnd: string | null;
  status: TeamMapStopStatus;
  /** Assigned engineer first, then any supporting engineers. Empty when unassigned. */
  userIds: string[];
}

export interface TeamMapPersonInput {
  id: string;
  name: string;
  /** On the field team, so listed even with nothing booked. */
  onTeam: boolean;
}

const clockFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const dayFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', weekday: 'short', day: 'numeric',
});
const monthFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', month: 'long' });

/** London "14:05" for an ISO timestamp, or null. */
export function londonClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? clockFmt.format(new Date(t)) : null;
}

/**
 * "Fri 10 Oct, 12:42". The month is cut from the LONG name rather than asked
 * for short, because Node and WebKit disagree about short September
 * (anti-pattern #41) and this string is rendered on both.
 */
function asOfLabelFor(now: Date): string {
  return `${dayFmt.format(now)} ${monthFmt.format(now).slice(0, 3)}, ${clockFmt.format(now)}`;
}

const STATUS_ORDER: Record<TeamMapStatus, number> = {
  'on-site': 0, 'on-the-way': 1, 'not-started': 2, finished: 3, 'nothing-booked': 4,
};

function round(n: number | null): number | null {
  return n == null || !Number.isFinite(n) ? null : Math.round(n * 1e5) / 1e5;
}

/** One person's place, from their jobs in time order. */
function placePerson(stops: readonly TeamMapStop[], jobs: readonly TeamMapJobInput[]): Pick<TeamMapPerson, 'status' | 'at' | 'clock'> {
  const idx = (pred: (s: TeamMapStop) => boolean) => stops.findIndex(pred);
  const onSite = idx(s => s.status === 'on-site');
  if (onSite !== -1) return { status: 'on-site', at: onSite, clock: londonClock(jobs[onSite].actualStart) };
  const onWay = idx(s => s.status === 'on-the-way');
  if (onWay !== -1) return { status: 'on-the-way', at: onWay, clock: stops[onWay].booked };
  const next = idx(s => s.status === 'not-started');
  if (next !== -1) return { status: 'not-started', at: next, clock: stops[next].booked };
  let last = -1;
  stops.forEach((s, i) => { if (s.status === 'finished') last = i; });
  if (last !== -1) return { status: 'finished', at: last, clock: londonClock(jobs[last].actualEnd) };
  return { status: 'nothing-booked', at: null, clock: null };
}

export interface TeamMapBuildInput {
  people: readonly TeamMapPersonInput[];
  /** Today's jobs, cancelled ones already removed. */
  jobs: readonly TeamMapJobInput[];
  viewerUserId: string;
  todayIso: string;
  now: Date;
  focusUserId?: string | null;
  open?: TeamMapBlock['open'];
}

/**
 * The block, or null when there is nobody to show (no team, and nobody with
 * work today) — a map of nothing answers nothing.
 */
export function teamMapBlock(input: TeamMapBuildInput): TeamMapBlock | null {
  const jobsByPerson = new Map<string, TeamMapJobInput[]>();
  let unassigned = 0;
  const ordered = [...input.jobs].sort((a, b) =>
    a.scheduledStart.localeCompare(b.scheduledStart) || a.id.localeCompare(b.id));
  for (const job of ordered) {
    const ids = [...new Set(job.userIds.filter(Boolean))];
    if (!ids.length) { unassigned += 1; continue; }
    for (const id of ids) {
      const list = jobsByPerson.get(id);
      if (list) list.push(job);
      else jobsByPerson.set(id, [job]);
    }
  }

  const people: TeamMapPerson[] = [];
  for (const p of input.people) {
    const jobs = jobsByPerson.get(p.id) ?? [];
    if (!jobs.length && !p.onTeam) continue;
    const all: TeamMapStop[] = jobs.map((j, i) => {
      const lat = round(j.lat);
      const lng = round(j.lng);
      const placed = lat != null && lng != null;
      return {
        jobId: j.id,
        n: i + 1,
        status: j.status,
        title: j.type?.trim() || 'Job',
        postcode: j.postcode?.trim() || null,
        booked: londonClock(j.scheduledStart),
        lat: placed ? lat : null,
        lng: placed ? lng : null,
        path: `/jobs/${j.id}`,
      };
    });
    const place = placePerson(all, jobs);
    // Keep the job that places them even when the day is longer than the cap,
    // so the row never names a job the block does not carry.
    let stops = all.slice(0, TEAM_MAP_MAX_STOPS);
    let at = place.at;
    if (at != null && at >= TEAM_MAP_MAX_STOPS) {
      stops = [...all.slice(0, TEAM_MAP_MAX_STOPS - 1), all[at]];
      at = stops.length - 1;
    }
    people.push({
      userId: p.id,
      name: p.name,
      isYou: p.id === input.viewerUserId,
      status: place.status,
      at,
      clock: place.clock,
      stops,
      stopsMore: all.length - stops.length,
    });
  }
  if (!people.length) return null;

  people.sort((a, b) =>
    STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    || Number(b.isYou) - Number(a.isYou)
    || a.name.localeCompare(b.name, 'en-GB'));

  // The person asked about is never the one cut off the end.
  let shown = people.slice(0, TEAM_MAP_MAX_PEOPLE);
  const focus = input.focusUserId ? people.find(p => p.userId === input.focusUserId) ?? null : null;
  if (focus && !shown.includes(focus)) shown = [...shown.slice(0, TEAM_MAP_MAX_PEOPLE - 1), focus];

  return {
    kind: 'team-map',
    date: input.todayIso,
    asOf: clockFmt.format(input.now),
    asOfLabel: asOfLabelFor(input.now),
    people: shown,
    more: people.length - shown.length,
    unassigned,
    focusUserId: focus?.userId ?? null,
    open: input.open ?? null,
  };
}

/**
 * The one person a name means, or null. Exact full name first, then a unique
 * person whose name starts with it at a word boundary ("Dave" → "Dave Lowe").
 * Two Daves is no answer: the map opens on everyone, which is still true,
 * rather than on a guess.
 */
export function resolveFocus(people: readonly Pick<TeamMapPersonInput, 'id' | 'name'>[], raw: unknown): string | null {
  const q = typeof raw === 'string' ? raw.trim().toLowerCase().replace(/\s+/g, ' ') : '';
  if (!q) return null;
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const exact = people.filter(p => norm(p.name) === q);
  if (exact.length === 1) return exact[0].id;
  if (exact.length > 1) return null;
  const starts = people.filter(p => {
    const n = norm(p.name);
    return n.startsWith(`${q} `) || n.split(' ').includes(q);
  });
  return starts.length === 1 ? starts[0].id : null;
}

/** Where a person's pin goes, or null when they are not on the map. */
export function personPoint(p: TeamMapPerson): { lat: number; lng: number } | null {
  if (p.at == null) return null;
  const s = p.stops[p.at];
  return s && s.lat != null && s.lng != null ? { lat: s.lat, lng: s.lng } : null;
}

/** Pin colours (status colours from the brand tokens), as Mapbox wants them: hex, no '#'. */
export const PIN_COLOURS: Record<TeamMapStopStatus, string> = {
  'on-site': '0f9e56',
  'on-the-way': '1a62b5',
  'not-started': 'c47f00',
  finished: '8a8786',
};

export interface MapPin {
  lat: number;
  lng: number;
  /** 1–99, drawn on the pin. */
  label: number;
  status: TeamMapStopStatus;
}

/**
 * The pins for a view. Everyone: one per person on the map, numbered by their
 * row. One person: each of their jobs today, numbered by their day.
 */
export function pinsFor(block: TeamMapBlock, show: string | 'all'): MapPin[] {
  if (show === 'all') {
    const pins: MapPin[] = [];
    block.people.forEach((p, i) => {
      const pt = personPoint(p);
      if (pt && p.status !== 'nothing-booked') pins.push({ ...pt, label: i + 1, status: p.status });
    });
    return pins;
  }
  const person = block.people.find(p => p.userId === show);
  if (!person) return [];
  return person.stops
    .filter((s): s is TeamMapStop & { lat: number; lng: number } => s.lat != null && s.lng != null)
    .map(s => ({ lat: s.lat, lng: s.lng, label: s.n, status: s.status }));
}

/** Mapbox draws later overlays on top; the live ones go last so they are never hidden. */
const DRAW_ORDER: Record<TeamMapStopStatus, number> = { finished: 0, 'not-started': 1, 'on-the-way': 2, 'on-site': 3 };

/**
 * A Mapbox Static Images URL for these pins, or null when there is no token or
 * nothing to pin. Called at RENDER with `process.env.NEXT_PUBLIC_MAPBOX_TOKEN`;
 * never stored. Several pins auto-fit; one pin gets a street-level zoom,
 * because `auto` on a single point zooms to the rooftop.
 */
export function staticMapUrl(
  pins: readonly MapPin[],
  token: string | null | undefined,
  size: { width: number; height: number } = { width: 360, height: 220 },
): string | null {
  if (!token || !pins.length) return null;
  const overlay = [...pins]
    .sort((a, b) => DRAW_ORDER[a.status] - DRAW_ORDER[b.status])
    .map(p => {
      const label = p.label >= 1 && p.label <= 99 ? `-${Math.floor(p.label)}` : '';
      return `pin-s${label}+${PIN_COLOURS[p.status]}(${p.lng.toFixed(5)},${p.lat.toFixed(5)})`;
    })
    .join(',');
  const view = pins.length === 1 ? `${pins[0].lng.toFixed(5)},${pins[0].lat.toFixed(5)},13` : 'auto';
  const query = pins.length === 1 ? '' : 'padding=36&';
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${overlay}/${view}/${size.width}x${size.height}@2x?${query}access_token=${encodeURIComponent(token)}`;
}

/**
 * One line for the oversight transcript: counts and states only, nothing a
 * customer or a member of staff could be identified by.
 */
export function summariseTeamMapBlock(block: TeamMapBlock): string {
  const count = (s: TeamMapStatus) => block.people.filter(p => p.status === s).length;
  const pinned = block.people.filter(p => personPoint(p) && p.status !== 'nothing-booked').length;
  const total = block.people.length + block.more;
  const parts = [
    `${total} ${total === 1 ? 'person' : 'people'}`,
    `${count('on-site')} on site`,
    `${count('on-the-way')} on the way`,
    `${count('not-started')} not started`,
    `${count('finished')} finished`,
    `${count('nothing-booked')} nothing booked`,
    `${pinned} pinned at a job (not GPS)`,
  ];
  if (block.more) parts.push(`${block.more} not shown`);
  if (block.unassigned) parts.push(`${block.unassigned} job${block.unassigned === 1 ? '' : 's'} unassigned`);
  if (block.focusUserId) parts.push('opened on one person');
  return `[team-map: ${parts.join(', ')}]`;
}

/**
 * The sentence appended to the TOOL RESULT so the model knows what is on
 * screen and does not read it out, or invent a position the map never had.
 */
export function teamMapResultNote(block: TeamMapBlock): string {
  const n = block.people.length;
  return `\n\nA map of today's work is on screen: ${n} ${n === 1 ? 'person' : 'people'}, each with what they are on or have next and its postcode. `
    + 'Pins sit at job addresses, not live GPS — never say where anyone physically is, how far away they are, or when they will arrive. '
    + 'Do not read the rows out; answer what was asked in a sentence.';
}
